import React, { useCallback, useContext, useEffect } from 'react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import messages from './QuotePage.messages';
import CoveragesPageComponent from "../Coverages/CoveragesPage";
import presentationMetadata from './QuotePage.metadata.json5';
import styles from './QuotePage.module.scss';
import InternalUseComponent from '../../components/subcomponents/InternalUse/InternalUseComponent';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;

    const { authHeader } = useAuthentication();
    const breakpoint = useContext(BreakpointTrackerContext);
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const selectOffering = submissionVM.value.lobData.perLine.selectedOffering_Ext
    const { pageKey } = currentStep;

    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const handleNext = useCallback(() => {
        const offeredQuotes = _.get(
            submissionVM.value,
            'quoteData.offeredQuotes[0].publicID'
        );

        _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

        return submissionVM;
    }, [submissionVM]);

    const navigateToCoverages = useCallback(() => {
        let redirectPath = ''

        switch (selectOffering) {
            case 'Cond':
                redirectPath = '/coveragesPage';
                break;
            case 'RisAssDwe':
                redirectPath = '/coveragesPage'
                break;
            case 'PerProInSto':
                redirectPath = '/coveragesPage'
                break;
            case 'MobHom':
                redirectPath = '/coveragesPage'
                break;
            default:
                break;
        }

        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === redirectPath
        );

        jumpTo(indexOfGeneralCoverages);
    }, [jumpTo, selectOffering, steps]);

    const getCell = (item, _index, property) => item[property.id];

    const getFormTableData = () => _.map(submissionVM?.value?.lobData?.perLine?.lineCoverages?.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString,
        },
        resolveComponentMap: {
            coveragesPageComponent: CoveragesPageComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            internalUseComponent: InternalUseComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const getProInStoAccordianTitle = useCallback((index) => (
        <div className={styles.accordianTitle}>
            <p>
                {`${translator(messages.stoFacNamValue)}${_.get(submissionVM.value, `lobData.perLine.coverables.propertyStorageCoverable.propertyInStorage[${index}].propertyDetails.locRisk.stoFacFie`)
                    }`}
            </p>
            <p>
                {`${translator(messages.stoFacLocValue)}${_.get(submissionVM.value, `lobData.perLine.coverables.propertyStorageCoverable.propertyInStorage[${index}].propertyDetails.locRisk.stoAddFie`)
                    }`}
            </p>
        </div>
    ), [submissionVM, translator]);

    const getTitleofAccordion = useCallback((index) => {
        if (selectOffering === 'Cond') {
            return _.get(submissionVM.value, `lobData.perLine.coverables.condoCoverable.condominiums[${index}].propertyDetails.accountLocFie.displayName`)
        }

        if (selectOffering === 'RisAssDwe') {
            return _.get(submissionVM.value, `lobData.perLine.coverables.riskAssistDwellingsCoverable.riskAssistDwellings[${index}].propertyDetails.accountLocFie.displayName`)
        }

        if (selectOffering === 'MobHom') {
            return _.get(submissionVM.value, `lobData.perLine.coverables.mobileHomeCoverable.mobileHomes[${index}].propertyDetails.accountLocFie.displayName`)
        }

        if (selectOffering === 'PerProInSto') {
            return getProInStoAccordianTitle(index)
        }
    }, [getProInStoAccordianTitle, selectOffering, submissionVM.value])

    const generatelocRiskseOverrides = useCallback(() => {

        const overrides = submissionVM.value.lobData.perLine.lineCoverages.locRiskCoverages.pprcoverages.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: getTitleofAccordion(index)
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.perLine.lineCoverages.locRiskCoverages.pprcoverages.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        return Object.assign({}, ...overrides);
    }, [getTitleofAccordion, submissionVM.value.lobData.perLine.lineCoverages.locRiskCoverages.pprcoverages, updateWizardData, wizardData]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        policyQuoteValDaysHead: {
            content: translator(messages.prQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.perLine.lineCoverages.forms_Ext
        },
        additionalQuoteComponent: {
            visible: _.get(submissionVM.value, 'lobData.perLine.internalUseOnly.ppraddConofPolExpos')?.length > 0,
            data: _.get(submissionVM, 'lobData.perLine.internalUseOnly') ? _.get(submissionVM, 'lobData.perLine.internalUseOnly.ppraddConofPolExpos') : [],
            isSubjectQuote: false
        },
        subjectQuoteComponent: {
            visible: _.get(submissionVM.value, 'lobData.perLine.internalUseOnly.pprsubofQuoExpos')?.length > 0,
            data: _.get(submissionVM, 'lobData.perLine.internalUseOnly') ? _.get(submissionVM, 'lobData.perLine.internalUseOnly.pprsubofQuoExpos') : [],
            isSubjectQuote: true
        },
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}>
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM.value}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
